import { formatUTCToLocalDate, utcISOToParisDate } from '../utils/timezone';

// Totals and date buckets are calculated by the same server policy as statistics.
export function getYearlyRevenueTotal(rows, year) { return rows?.periodTotals?.[String(year)]?.expected || 0; }
export function getMonthlyRevenueTotal(rows, year, month) { return rows?.periodTotals?.[`${year}-${String(month).padStart(2, '0')}`]?.expected || 0; }
export function getYearlyDepositTotal(rows, year) { return rows?.periodTotals?.[String(year)]?.deposits || 0; }
export function getMonthlyDepositTotal(rows, year, month) { return rows?.periodTotals?.[`${year}-${String(month).padStart(2, '0')}`]?.deposits || 0; }

export function formatRevenueLabel(amount) {
  if (amount === 0) {
    return '-';
  }

  if (amount >= 1000000) {
    return `${(amount / 1000000).toFixed(1)}M€`;
  }

  if (amount >= 1000) {
    return `${(amount / 1000).toFixed(1)}k€`;
  }

  return `${amount}€`;
}

export function getClosestFutureReservationDate(reservations) {
  const today = new Date();
  const todayOnly = new Date(today.getFullYear(), today.getMonth(), today.getDate());

  const futureDates = (reservations || [])
    .filter((reservation) => reservation.relatedEvent?.start_time)
    .map((reservation) => {
      try {
        const eventDate = utcISOToParisDate(reservation.relatedEvent.start_time);
        const eventDateOnly = new Date(eventDate.getFullYear(), eventDate.getMonth(), eventDate.getDate());
        return { reservation, date: eventDateOnly };
      } catch {
        return null;
      }
    })
    .filter((item) => item && item.date >= todayOnly)
    .sort((left, right) => left.date.getTime() - right.date.getTime());

  return futureDates.length > 0 ? futureDates[0].date : null;
}

export function sortAndFilterManualReservations(reservations, options) {
  const {
    manualSearch,
    manualCategory,
    manualStatus,
    selectedYear,
    selectedMonth,
    sortField,
    sortOrder
  } = options;

  if (!reservations?.length) {
    return [];
  }

  const filteredReservations = reservations.filter((reservation) => {
    if (manualSearch.trim()) {
      const searchTerm = manualSearch.toLowerCase();
      let found = false;

      switch (manualCategory) {
        case 'client_name':
          found = (reservation.client_name || '').toLowerCase().includes(searchTerm);
          break;
        case 'client_instagram':
          found = (reservation.client_instagram || '').toLowerCase().includes(searchTerm);
          break;
        case 'memo':
          found = (reservation.memo || '').toLowerCase().includes(searchTerm);
          break;
        default:
          found = true;
      }

      if (!found) {
        return false;
      }
    }

    if (manualStatus !== 'all') {
      switch (manualStatus) {
        case 'deposit_pending':
          if (!(Number(reservation.deposit_amount) > 0) || reservation.deposit_received) {
            return false;
          }
          break;
        case 'deposit_received':
          if (!(Number(reservation.deposit_amount) > 0) || !reservation.deposit_received) {
            return false;
          }
          break;
        case 'no_deposit':
          if (Number(reservation.deposit_amount) > 0) {
            return false;
          }
          break;
        default:
          break;
      }
    }

    if (reservation.accounting) return reservation.accounting.period === `${selectedYear}-${String(selectedMonth).padStart(2, '0')}`;
    const startDate = new Date(selectedYear, selectedMonth - 1, 1);
    const endDate = new Date(selectedYear, selectedMonth, 0);

    try {
      let reservationDate;

      if (reservation.relatedEvent?.start_time) {
        reservationDate = new Date(reservation.relatedEvent.start_time);
      } else if (reservation.created_at) {
        reservationDate = new Date(reservation.created_at);
      } else {
        return false;
      }

      const reservationDateOnly = new Date(
        reservationDate.getFullYear(),
        reservationDate.getMonth(),
        reservationDate.getDate()
      );
      const startDateOnly = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
      const endDateOnly = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate());

      if (reservationDateOnly < startDateOnly || reservationDateOnly > endDateOnly) {
        return false;
      }
    } catch (error) {
      console.error('날짜 필터링 에러:', error, reservation);
      return false;
    }

    return true;
  });

  return filteredReservations.sort((left, right) => {
    let leftValue;
    let rightValue;

    switch (sortField) {
      case 'deposit_status': {
        const getDepositStatusValue = (reservation) => {
          if (!(Number(reservation.deposit_amount) > 0)) {
            return 0;
          }

          return reservation.deposit_received ? 2 : 1;
        };

        leftValue = getDepositStatusValue(left);
        rightValue = getDepositStatusValue(right);
        break;
      }
      case 'event_start_time':
        leftValue = left.relatedEvent?.start_time || '';
        rightValue = right.relatedEvent?.start_time || '';
        break;
      case 'client_instagram':
        leftValue = (left.client_instagram || '').toLowerCase();
        rightValue = (right.client_instagram || '').toLowerCase();
        break;
      case 'client_name':
        leftValue = (left.client_name || '').toLowerCase();
        rightValue = (right.client_name || '').toLowerCase();
        break;
      case 'price':
        leftValue = left.accounting?.expected_eur ?? 0;
        rightValue = right.accounting?.expected_eur ?? 0;
        break;
      case 'deposit_amount':
        leftValue = left.deposit_amount || 0;
        rightValue = right.deposit_amount || 0;
        break;
      case 'memo':
        leftValue = (left.memo || '').toLowerCase();
        rightValue = (right.memo || '').toLowerCase();
        break;
      case 'deposit_received_date':
        leftValue = left.deposit_received_date || '';
        rightValue = right.deposit_received_date || '';
        break;
      case 'deposit_platform':
        leftValue = (left.deposit_platform || '').toLowerCase();
        rightValue = (right.deposit_platform || '').toLowerCase();
        break;
      case 'created_at':
        leftValue = left.created_at || '';
        rightValue = right.created_at || '';
        break;
      case 'reservation_type':
        leftValue = (left.reservation_type || '').toLowerCase();
        rightValue = (right.reservation_type || '').toLowerCase();
        break;
      default:
        return 0;
    }

    if (leftValue < rightValue) {
      return sortOrder === 'asc' ? -1 : 1;
    }

    if (leftValue > rightValue) {
      return sortOrder === 'asc' ? 1 : -1;
    }

    return 0;
  });
}

function formatReservationDateForCsv(isoString) {
  if (!isoString) {
    return '-';
  }

  try {
    const date = utcISOToParisDate(isoString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = String(date.getFullYear()).slice(-2);
    const hour = String(date.getHours()).padStart(2, '0');
    const minute = String(date.getMinutes()).padStart(2, '0');
    const weekdays = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];

    return `${day}/${month}/${year} ${weekdays[date.getDay()]} ${hour}:${minute}`;
  } catch {
    return '-';
  }
}

function formatDateTimeForCsv(isoString) {
  if (!isoString) {
    return '-';
  }

  try {
    return utcISOToParisDate(isoString).toLocaleString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
  } catch {
    return '-';
  }
}

function getDepositPlatformLabelForCsv(platform) {
  switch (platform) {
    case 'paypal':
      return 'PayPal';
    case 'lydia':
      return 'Lydia';
    case 'cash':
      return 'Cash';
    case 'bank_transfer':
      return 'Bank Transfer';
    case 'other':
      return 'Other';
    default:
      return platform || '-';
  }
}

function downloadTextFile(content, filename, mimeType) {
  const blob = new Blob([content], { type: mimeType });
  const link = document.createElement('a');

  if (link.download === undefined) {
    return;
  }

  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export function downloadManualReservationsCsv(reservations) {
  const headers = [
    'Deposit Status',
    'Reservation Date',
    'Type',
    'Instagram',
    'Customer Name',
    'Price Range',
    'Memo',
    'Deposit Received Date',
    'Deposit Platform',
    'Created Date',
    'Email',
    'Phone',
    'Deposit Amount',
    'Event Title',
    'Event Start',
    'Event End'
  ];

  const csvData = reservations.map((reservation) => {
    const depositStatus = reservation.deposit_amount
      ? `${reservation.deposit_amount}${reservation.deposit_received ? ' (Received)' : ' (Not Received)'}`
      : '-';
    const priceRange = reservation.min_price && reservation.max_price
      ? `${reservation.min_price} ~ ${reservation.max_price}`
      : reservation.min_price || reservation.max_price || '-';

    return [
      depositStatus,
      formatReservationDateForCsv(reservation.relatedEvent?.start_time),
      reservation.reservation_type === 'personal_project' ? 'personal' : (reservation.reservation_type || '-'),
      reservation.client_instagram || '-',
      reservation.client_name || '-',
      priceRange,
      (reservation.memo || '-').replace(/[\r\n]+/g, ' '),
      formatDateTimeForCsv(reservation.deposit_received_date),
      getDepositPlatformLabelForCsv(reservation.deposit_platform),
      reservation.created_at ? formatUTCToLocalDate(reservation.created_at, 'en-GB') : '-',
      reservation.client_email || '-',
      reservation.client_phone || '-',
      reservation.deposit_amount || '-',
      reservation.relatedEvent?.title || '-',
      formatDateTimeForCsv(reservation.relatedEvent?.start_time),
      formatDateTimeForCsv(reservation.relatedEvent?.end_time)
    ];
  });

  const csvContent = [headers, ...csvData]
    .map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(','))
    .join('\n');

  const now = new Date();
  const dateString = now.toISOString().slice(0, 10).replace(/-/g, '');
  const timeString = now.toTimeString().slice(0, 8).replace(/:/g, '');
  const filename = `manual_reservations_${dateString}_${timeString}.csv`;

  downloadTextFile(`\uFEFF${csvContent}`, filename, 'text/csv;charset=utf-8;');
}
